import { mutate } from 'swr'

import {
  patchJson,
  postJson,
  postJsonWithHeaders,
  putJson,
  requestDelete,
} from '../../../data/fetchers'
import {
  EInvoiceAttachment,
  EInvoiceDto,
  PatchRequest,
} from '../../../data/models'
import { InvoiceLineType } from '../../../data/models/_generatedUblInvoice'

// Create e-invoice line
export async function requestCreateEInvoiceLine(
  eInvoiceId: string,
  lineDto: InvoiceLineType,
) {
  const result = await postJson(`/einvoices/${eInvoiceId}/lines`, lineDto)
  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(`/einvoices/${eInvoiceId}/lines`.toLowerCase()),
  )

  return result
}

// Update e-invoice line
export async function requestUpdateEInvoiceLine(
  eInvoiceId: string,
  lineId: string,
  lineDto: PatchRequest<InvoiceLineType>,
) {
  const result = await patchJson(
    `/einvoices/${eInvoiceId}/lines/${lineId}`,
    lineDto,
    { 'Content-Type': 'application/json-patch+json' },
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(`/einvoices/${eInvoiceId}/lines`.toLowerCase()),
  )

  return result
}

// Delete e-invoice line
export async function requestDeleteEInvoiceLine(
  eInvoiceId: string,
  lineId: string,
) {
  const result = await requestDelete(`/einvoices/${eInvoiceId}/lines/${lineId}`)

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key
      .toLowerCase()
      .startsWith(`/einvoices/${eInvoiceId}/lines/`.toLowerCase()),
  )

  return result
}

// Upload e-invoice attachment
export async function requestUploadEInvoiceAttachment(
  eInvoiceId: string,
  processId: string,
  attachment: { attachment: File; description: string },
) {
  const { attachment: file, description } = attachment

  const attObj: Omit<EInvoiceAttachment, 'id'> = {
    FileName: file.name,
    DocumentType: 'AdditionalDocument',
    Description: description,
    MimeType: file.type,
    FileSizeInBytes: file.size,
  }

  const formData = new FormData()
  formData.append('processId', processId)
  formData.append('file', file)
  formData.append('attachment', JSON.stringify(attObj))

  const result = await postJson(
    `/einvoices/${eInvoiceId}/attachments`,
    formData,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key.toLowerCase().startsWith(`/einvoices/${eInvoiceId}`.toLowerCase()),
  )

  return result
}

// Update e-invoice attachment
export async function requestUpdateEInvoiceAttachment(
  eInvoiceId: string,
  attachmentId: string,
  attachment: EInvoiceAttachment,
) {
  const result = await putJson(
    `/einvoices/${eInvoiceId}/attachments/${attachmentId}`,
    attachment,
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key.toLowerCase().startsWith(`/einvoices/${eInvoiceId}`.toLowerCase()),
  )

  return result
}

// Delete e-invoice attachment
export async function requestDeleteEInvoiceAttachment(
  eInvoiceId: string,
  attachmentId: string,
  attachmentUri: string,
) {
  const result = await requestDelete(
    `/einvoices/${eInvoiceId}/attachments/${attachmentId}`,
    {
      'Xia-AttachmentUri': encodeURI(attachmentUri),
    },
  )

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key.toLowerCase().startsWith(`/einvoices/${eInvoiceId}`.toLowerCase()),
  )

  return result
}

// Create e-invoice
export async function requestCreateEInvoice(
  eInvoiceFile: File,
  isTrial = false,
): Promise<any> {
  const params = new URLSearchParams()
  if (isTrial) {
    params.append('isTrial', isTrial.toString())
  }

  const queryString = params.toString()
  let url = '/einvoices'
  url = queryString ? `${url}?${queryString}` : url

  const formData = new FormData()
  formData.append('eInvoiceFile', eInvoiceFile)

  const result = await postJsonWithHeaders(url, formData)

  return result
}

// Update e-invoice
export async function requestUpdateEInvoice(
  eInvoiceId: string,
  eInvoiceDto: EInvoiceDto,
): Promise<any> {
  const result = await putJson(`/einvoices/${eInvoiceId}`, eInvoiceDto)

  // HACK: Should be called in components in order to make OptimisticUi possible
  mutate((key: string) =>
    key.toLowerCase().startsWith(`/einvoices/${eInvoiceId}`.toLowerCase()),
  )

  return result
}

export async function requestValidateEInvoice(
  eInvoiceId: string,
): Promise<any> {
  const result = await postJsonWithHeaders(
    `/einvoices/${eInvoiceId}/runbusinessrules`,
    {},
  )

  return result
}

// Complete e-invoice
export async function requestCompleteEInvoice(
  eInvoiceId: string,
): Promise<any> {
  return await patchJson(`/einvoices/${eInvoiceId}/complete`)
}

// Delete e-invoice
export async function requestDeleteEInvoice(eInvoiceId: string): Promise<any> {
  return await requestDelete(`/einvoices/${eInvoiceId}`)
}
